import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  Download,
  Eye,
  Edit,
  Trash2,
  X,
  Upload,
  FileText,
  ExternalLink,
  Package,
  TrendingUp,
  ShoppingCart,
  Building2,
  Calendar,
  Phone,
  Mail,
  Globe,
  MapPin,
  Loader2,
  ChevronDown,
  ChevronUp,
  Lightbulb,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Common calibers for dropdown
const COMMON_CALIBERS = [
  '9mm',
  '.22 LR',
  '.223 Rem',
  '.308 Win',
  '6.5 Creedmoor',
  '.45 ACP',
  '.40 S&W',
  '12 Gauge',
  '.38 Special',
  '.357 Magnum',
  '30-06',
  '7.62x39',
  '5.56 NATO'
];

const AmmoManagement = () => {
  const { isAdmin, isOrgAdmin, isRangeOfficer, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const hasAccess = isAdmin || isOrgAdmin || isRangeOfficer;
  // Only admins/superadmin can jump directly to Ammo-innstillinger
  const canOpenAmmoSettings = isAdmin || isSuperAdmin;
  
  // Tab state
  const [activeTab, setActiveTab] = useState('suggestions');
  
  // Data states
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  
  // Modal states
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showDocumentPreview, setShowDocumentPreview] = useState(null);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [editingPurchase, setEditingPurchase] = useState(null);
  
  // Supplier form
  const [supplierForm, setSupplierForm] = useState({
    name: '',
    contact_person: '',
    contact_email: '',
    contact_phone: '',
    website_url: '',
    address: '',
    notes: ''
  });
  
  // Purchase form
  const [purchaseForm, setPurchaseForm] = useState({
    supplier_id: '',
    supplier_name: '',
    purchase_date: new Date().toISOString().split('T')[0],
    calibers: [{ caliber: '', quantity: '', unit_price: '' }],
    total_amount: '',
    invoice_number: '',
    notes: '',
    documents: []
  });
  
  // File upload state
  const [uploading, setUploading] = useState(false);

  // Load data on mount
  useEffect(() => {
    if (hasAccess) {
      loadAllData();
    }
  }, [hasAccess]);

  const loadAllData = async () => {
    setLoading(true);
    await Promise.all([
      loadStats(),
      loadSuppliers(),
      loadPurchases()
    ]);
    setLoading(false);
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/club-operations/ammo/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading ammo stats:', error);
    }
  };

  const loadSuppliers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/club-operations/ammo/suppliers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuppliers(response.data);
    } catch (error) {
      console.error('Error loading suppliers:', error);
    }
  };

  const loadPurchases = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/club-operations/ammo/purchases`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPurchases(response.data);
    } catch (error) {
      console.error('Error loading purchases:', error);
    }
  };

  // Supplier handlers
  const handleAddSupplier = () => {
    setEditingSupplier(null);
    setSupplierForm({
      name: '',
      contact_person: '',
      contact_email: '',
      contact_phone: '',
      website_url: '',
      address: '',
      notes: ''
    });
    setShowSupplierModal(true);
  };

  const handleEditSupplier = (supplier) => {
    setEditingSupplier(supplier);
    setSupplierForm({
      name: supplier.name || '',
      contact_person: supplier.contact_person || '',
      contact_email: supplier.contact_email || '',
      contact_phone: supplier.contact_phone || '',
      website_url: supplier.website_url || '',
      address: supplier.address || '',
      notes: supplier.notes || ''
    });
    setShowSupplierModal(true);
  };

  const handleSaveSupplier = async () => {
    if (!supplierForm.name.trim()) {
      toast.error('Navn er påkrevd');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      if (editingSupplier) {
        await axios.put(
          `${API_BASE}/api/club-operations/ammo/suppliers/${editingSupplier.id}`,
          supplierForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Leverandør oppdatert');
      } else {
        await axios.post(
          `${API_BASE}/api/club-operations/ammo/suppliers`,
          supplierForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Leverandør opprettet');
      }
      setShowSupplierModal(false);
      loadSuppliers();
    } catch (error) {
      console.error('Error saving supplier:', error);
      toast.error('Kunne ikke lagre leverandør');
    }
  };

  const handleDeleteSupplier = async (supplierId) => {
    if (!window.confirm('Er du sikker på at du vil slette denne leverandøren?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/api/club-operations/ammo/suppliers/${supplierId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Leverandør slettet');
      loadSuppliers();
    } catch (error) {
      console.error('Error deleting supplier:', error);
      toast.error('Kunne ikke slette leverandør');
    }
  };

  // Purchase handlers
  const handleAddPurchase = () => {
    setEditingPurchase(null);
    setPurchaseForm({
      supplier_id: '',
      supplier_name: '',
      purchase_date: new Date().toISOString().split('T')[0],
      calibers: [{ caliber: '', quantity: '', unit_price: '' }],
      total_amount: '',
      invoice_number: '',
      notes: '',
      documents: []
    });
    setShowPurchaseModal(true);
  };

  const handleEditPurchase = (purchase) => {
    setEditingPurchase(purchase);
    setPurchaseForm({
      supplier_id: purchase.supplier_id || '',
      supplier_name: purchase.supplier_name || '',
      purchase_date: purchase.purchase_date || '',
      calibers: purchase.calibers?.length > 0 ? purchase.calibers : [{ caliber: '', quantity: '', unit_price: '' }],
      total_amount: purchase.total_amount || '',
      invoice_number: purchase.invoice_number || '',
      notes: purchase.notes || '',
      documents: purchase.documents || []
    });
    setShowPurchaseModal(true);
  };

  const handleSavePurchase = async () => {
    if (!purchaseForm.purchase_date) {
      toast.error('Innkjøpsdato er påkrevd');
      return;
    }
    
    const validCalibers = purchaseForm.calibers.filter(c => c.caliber && c.quantity);
    if (validCalibers.length === 0) {
      toast.error('Legg til minst ett kaliber med mengde');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      // Calculate total from calibers
      const calculatedTotal = validCalibers.reduce((sum, c) => {
        const qty = parseInt(c.quantity) || 0;
        const price = parseFloat(c.unit_price) || 0;
        return sum + (qty * price);
      }, 0);
      
      const data = {
        supplier_id: purchaseForm.supplier_id || null,
        supplier_name: purchaseForm.supplier_name || null,
        purchase_date: purchaseForm.purchase_date,
        calibers: validCalibers.map(c => ({
          caliber: c.caliber,
          quantity: parseInt(c.quantity) || 0,
          unit_price: parseFloat(c.unit_price) || 0
        })),
        total_amount: calculatedTotal || null,
        invoice_number: purchaseForm.invoice_number || null,
        notes: purchaseForm.notes || null,
        documents: purchaseForm.documents || []
      };
      
      if (editingPurchase) {
        await axios.put(
          `${API_BASE}/api/club-operations/ammo/purchases/${editingPurchase.id}`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Innkjøp oppdatert');
      } else {
        await axios.post(
          `${API_BASE}/api/club-operations/ammo/purchases`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Innkjøp registrert');
      }
      setShowPurchaseModal(false);
      loadPurchases();
      loadStats();
    } catch (error) {
      console.error('Error saving purchase:', error);
      toast.error(error.response?.data?.detail || 'Kunne ikke lagre innkjøp');
    }
  };

  const handleDeletePurchase = async (purchaseId) => {
    if (!window.confirm('Er du sikker på at du vil slette dette innkjøpet?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/api/club-operations/ammo/purchases/${purchaseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Innkjøp slettet');
      loadPurchases();
      loadStats();
    } catch (error) {
      console.error('Error deleting purchase:', error);
      toast.error('Kunne ikke slette innkjøp');
    }
  };

  // Caliber row handlers
  const addCaliberRow = () => {
    setPurchaseForm(prev => ({
      ...prev,
      calibers: [...prev.calibers, { caliber: '', quantity: '', unit_price: '' }]
    }));
  };

  const updateCaliberRow = (index, field, value) => {
    setPurchaseForm(prev => {
      const updated = [...prev.calibers];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, calibers: updated };
    });
  };

  const removeCaliberRow = (index) => {
    if (purchaseForm.calibers.length > 1) {
      setPurchaseForm(prev => ({
        ...prev,
        calibers: prev.calibers.filter((_, i) => i !== index)
      }));
    }
  };

  // Document upload
  const handleDocumentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!editingPurchase?.id) {
      toast.error('Lagre innkjøpet først, deretter last opp dokumenter');
      return;
    }
    
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API_BASE}/api/club-operations/ammo/purchases/${editingPurchase.id}/upload-document`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setPurchaseForm(prev => ({
        ...prev,
        documents: [...prev.documents, response.data.url]
      }));
      toast.success('Dokument lastet opp');
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Kunne ikke laste opp dokument');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (docUrl) => {
    if (!editingPurchase?.id) return;
    if (!window.confirm('Er du sikker på at du vil slette dette dokumentet?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_BASE}/api/club-operations/ammo/purchases/${editingPurchase.id}/document`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { document_url: docUrl }
        }
      );
      
      setPurchaseForm(prev => ({
        ...prev,
        documents: prev.documents.filter(d => d !== docUrl)
      }));
      toast.success('Dokument slettet');
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Kunne ikke slette dokument');
    }
  };

  // Calculate totals
  const calculateTotal = () => {
    return purchaseForm.calibers.reduce((sum, c) => {
      const qty = parseInt(c.quantity) || 0;
      const price = parseFloat(c.unit_price) || 0;
      return sum + (qty * price);
    }, 0);
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nb-NO', {
      style: 'currency',
      currency: 'NOK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('nb-NO', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  if (!hasAccess) {
    return (
      <div className="text-center py-12">
        <Package className="w-16 h-16 text-gray-600 mx-auto mb-4" />
        <p className="text-gray-400">Du har ikke tilgang til denne modulen</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-yellow-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Sub-navigation tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-800 border-gray-700 flex-wrap h-auto">
          <TabsTrigger value="suggestions" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-gray-900">
            <Lightbulb className="w-4 h-4 mr-2" />
            Bestillingsforslag
          </TabsTrigger>
          <TabsTrigger value="purchases" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-gray-900">
            <ShoppingCart className="w-4 h-4 mr-2" />
            Innkjøp
          </TabsTrigger>
          <TabsTrigger value="planning" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-gray-900">
            <TrendingUp className="w-4 h-4 mr-2" />
            Planlegging
          </TabsTrigger>
        </TabsList>

        {/* Bestillingsforslag Tab */}
        <TabsContent value="suggestions" className="mt-6">
          <div className="space-y-6">
            {/* Low Stock Alert */}
            {stats?.low_stock_count > 0 && (
              <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-500/30 rounded-full flex items-center justify-center">
                    <Package className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <p className="text-red-400 font-medium">
                      {stats.low_stock_count} kalibre har lav beholdning
                    </p>
                    <p className="text-gray-400 text-sm">
                      Se lagerstatus under for detaljer
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Inventory Status Cards */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Package className="w-5 h-5 text-blue-400" />
                      Lagerstatus
                    </CardTitle>
                    <CardDescription className="text-gray-400">
                      Gjenværende beholdning for aktiverte kalibre
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {canOpenAmmoSettings && (
                      <button
                        onClick={() => navigate('/admin?tab=settings#ammo-salg')}
                        className="flex items-center gap-2 px-3 py-2 bg-yellow-500/20 text-yellow-300 hover:bg-yellow-500/30 border border-yellow-500/40 rounded-lg text-sm font-medium transition-colors"
                        title="Åpne Ammunisjonsinnstillinger"
                        data-testid="shortcut-ammo-settings"
                      >
                        <img src="/patron-symbol.svg" alt="" className="h-4 w-4" />
                        <span>Ammunisjonsinnstillinger</span>
                      </button>
                    )}
                    <button
                      onClick={loadStats}
                      className="p-2 bg-gray-700 rounded-lg text-gray-300 hover:bg-gray-600"
                      title="Oppdater"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {stats?.inventory_status?.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {stats.inventory_status.map((item, idx) => (
                      <div
                        key={idx}
                        className={`p-4 rounded-lg border ${
                          item.status === 'empty' || item.status === 'critical'
                            ? 'bg-red-500/10 border-red-500/50'
                            : item.status === 'low'
                            ? 'bg-orange-500/10 border-orange-500/50'
                            : item.status === 'medium'
                            ? 'bg-yellow-500/10 border-yellow-500/50'
                            : 'bg-green-500/10 border-green-500/50'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-white font-medium">{item.caliber}</span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            item.status === 'empty' || item.status === 'critical'
                              ? 'bg-red-500/30 text-red-300'
                              : item.status === 'low'
                              ? 'bg-orange-500/30 text-orange-300'
                              : item.status === 'medium'
                              ? 'bg-yellow-500/30 text-yellow-300'
                              : 'bg-green-500/30 text-green-300'
                          }`}>
                            {item.status_label}
                          </span>
                        </div>
                        <p className="text-2xl font-bold text-white mb-1">
                          {item.current_stock_boxes.toLocaleString()}
                          <span className="text-sm font-normal text-gray-400 ml-1">esker</span>
                        </p>
                        <p className="text-gray-400 text-sm">
                          {item.current_stock_rounds.toLocaleString()} skudd
                        </p>
                        <div className="mt-3 pt-3 border-t border-gray-700">
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-500">Gj.snitt/mnd:</span>
                            <span className="text-gray-400">{item.avg_monthly_consumption} esker</span>
                          </div>
                          {item.months_until_empty !== null && (
                            <div className="flex justify-between text-xs mt-1">
                              <span className="text-gray-500">Rekker til:</span>
                              <span className={`${
                                item.months_until_empty < 2 ? 'text-red-400' : 'text-gray-400'
                              }`}>
                                ~{item.months_until_empty} mnd
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Package className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400">Ingen aktiverte kalibre funnet</p>
                    <p className="text-gray-500 text-sm mt-2">
                      Aktiver kalibre under Admin → Ammo-salg
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Order Suggestions Card */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Lightbulb className="w-5 h-5 text-yellow-500" />
                      Innkjøpsanbefalinger
                    </CardTitle>
                    <CardDescription className="text-gray-400">
                      {stats?.is_spring_season 
                        ? 'Sesongstart - Anbefalt økt beholdning for våren'
                        : 'Basert på lagerstatus og historisk forbruk'}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {stats?.order_suggestions?.length > 0 ? (
                  <div className="space-y-3">
                    {stats.order_suggestions.map((suggestion, idx) => (
                      <div
                        key={idx}
                        className={`flex items-center justify-between p-4 rounded-lg border ${
                          suggestion.priority === 'high'
                            ? 'bg-red-500/10 border-red-500/50'
                            : suggestion.priority === 'medium'
                            ? 'bg-orange-500/10 border-orange-500/50'
                            : suggestion.priority === 'low'
                            ? 'bg-yellow-500/10 border-yellow-500/50'
                            : 'bg-gray-700/50 border-gray-600'
                        }`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-white font-medium text-lg">{suggestion.caliber}</p>
                            {suggestion.priority === 'high' && (
                              <span className="px-2 py-0.5 bg-red-500/30 text-red-300 rounded text-xs font-medium">
                                Haster
                              </span>
                            )}
                          </div>
                          <p className="text-gray-400 text-sm">{suggestion.reason}</p>
                          <p className="text-gray-500 text-xs mt-1">
                            Nåværende: {suggestion.current_stock} esker • 
                            Forbruk: {suggestion.avg_monthly_consumption}/mnd
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-yellow-400 font-bold text-xl">
                            {suggestion.suggested_quantity.toLocaleString()} stk
                          </p>
                          <p className="text-gray-400 text-sm">
                            Est. {formatCurrency(suggestion.estimated_cost)}
                          </p>
                          {suggestion.last_price_per_unit > 0 && (
                            <p className="text-gray-500 text-xs">
                              Siste pris: {formatCurrency(suggestion.last_price_per_unit)}/stk
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <ShoppingCart className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-green-400 font-medium">Alt på lager!</p>
                    <p className="text-gray-500 text-sm mt-2">
                      Ingen kalibre trenger påfyll akkurat nå
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="pt-6">
                  <p className="text-gray-400 text-sm">Totalt brukt</p>
                  <p className="text-2xl font-bold text-green-400">
                    {formatCurrency(stats?.total_spent || 0)}
                  </p>
                  <p className="text-gray-500 text-xs mt-1">
                    fra {stats?.total_purchases || 0} innkjøp
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="pt-6">
                  <p className="text-gray-400 text-sm">Aktiverte kalibre</p>
                  <p className="text-2xl font-bold text-blue-400">
                    {stats?.enabled_calibers_count || 0}
                  </p>
                  <p className="text-gray-500 text-xs mt-1">
                    i varelageret
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="pt-6">
                  <p className="text-gray-400 text-sm">Kalibre kjøpt</p>
                  <p className="text-2xl font-bold text-purple-400">
                    {Object.keys(stats?.caliber_stats || {}).length}
                  </p>
                  <p className="text-gray-500 text-xs mt-1">
                    forskjellige typer
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="pt-6">
                  <p className="text-gray-400 text-sm">Lav beholdning</p>
                  <p className={`text-2xl font-bold ${
                    (stats?.low_stock_count || 0) > 0 ? 'text-red-400' : 'text-green-400'
                  }`}>
                    {stats?.low_stock_count || 0}
                  </p>
                  <p className="text-gray-500 text-xs mt-1">
                    kalibre trenger påfyll
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Innkjøp Tab */}
        <TabsContent value="purchases" className="mt-6">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <CardTitle className="text-white flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5 text-yellow-500" />
                    Innkjøpshistorikk
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    Registrer og se tidligere ammunisjonsinnkjøp
                  </CardDescription>
                </div>
                <button
                  onClick={handleAddPurchase}
                  className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Nytt innkjøp
                </button>
                {canOpenAmmoSettings && (
                  <button
                    onClick={() => navigate('/admin?tab=settings#ammo-salg')}
                    className="p-1.5 opacity-70 hover:opacity-100 transition-opacity"
                    title="Åpne Ammo-innstillinger"
                    data-testid="link-to-ammo-settings"
                  >
                    <img src="/patron-symbol.svg" alt="Ammo-innstillinger" className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {purchases.length > 0 ? (
                <div className="space-y-4">
                  {purchases.map((purchase) => (
                    <div
                      key={purchase.id}
                      className="bg-gray-700/50 rounded-lg p-4"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="text-white font-medium">
                            {purchase.supplier_name || 'Ukjent leverandør'}
                          </p>
                          <p className="text-gray-400 text-sm flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(purchase.purchase_date)}
                            {purchase.invoice_number && (
                              <span className="ml-2">• Faktura: {purchase.invoice_number}</span>
                            )}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEditPurchase(purchase)}
                            className="p-2 text-gray-400 hover:text-white hover:bg-gray-600 rounded-lg transition-colors"
                            title="Rediger"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeletePurchase(purchase.id)}
                            className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-600 rounded-lg transition-colors"
                            title="Slett"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      
                      {/* Calibers */}
                      <div className="flex flex-wrap gap-2 mb-3">
                        {purchase.calibers?.map((cal, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 bg-gray-600 rounded text-sm text-gray-200"
                          >
                            {cal.caliber}: {cal.quantity?.toLocaleString()} stk
                            {cal.unit_price > 0 && (
                              <span className="text-gray-400 ml-1">
                                @ {formatCurrency(cal.unit_price)}
                              </span>
                            )}
                          </span>
                        ))}
                      </div>
                      
                      {/* Documents */}
                      {purchase.documents?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t border-gray-600">
                          {purchase.documents.map((doc, idx) => (
                            <a
                              key={idx}
                              href={doc}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-sm hover:bg-blue-500/30"
                            >
                              <FileText className="w-3 h-3" />
                              Dokument {idx + 1}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          ))}
                        </div>
                      )}
                      
                      {/* Total */}
                      <div className="mt-3 pt-3 border-t border-gray-600 flex justify-between items-center">
                        <span className="text-gray-400 text-sm">Total:</span>
                        <span className="text-green-400 font-bold">
                          {formatCurrency(purchase.total_amount)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <ShoppingCart className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">Ingen innkjøp registrert</p>
                  <button
                    onClick={handleAddPurchase}
                    className="mt-4 text-yellow-500 hover:text-yellow-400"
                  >
                    Registrer ditt første innkjøp
                  </button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Planlegging Tab */}
        <TabsContent value="planning" className="mt-6">
          <div className="space-y-6">
            {/* Suppliers Section */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-yellow-500" />
                      Leverandører
                    </CardTitle>
                    <CardDescription className="text-gray-400">
                      Administrer kontaktinformasjon for ammunisjonsleverandører
                    </CardDescription>
                  </div>
                  <button
                    onClick={handleAddSupplier}
                    className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Ny leverandør
                  </button>
                </div>
              </CardHeader>
              <CardContent>
                {suppliers.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {suppliers.map((supplier) => (
                      <div
                        key={supplier.id}
                        className="bg-gray-700/50 rounded-lg p-4"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="text-white font-medium text-lg">{supplier.name}</h3>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleEditSupplier(supplier)}
                              className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-600 rounded transition-colors"
                              title="Rediger"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteSupplier(supplier.id)}
                              className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-gray-600 rounded transition-colors"
                              title="Slett"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        
                        <div className="space-y-1 text-sm">
                          {supplier.contact_person && (
                            <p className="text-gray-300 flex items-center gap-2">
                              <span className="text-gray-500">Kontakt:</span>
                              {supplier.contact_person}
                            </p>
                          )}
                          {supplier.contact_phone && (
                            <p className="text-gray-300 flex items-center gap-2">
                              <Phone className="w-3 h-3 text-gray-500" />
                              <a href={`tel:${supplier.contact_phone}`} className="hover:text-yellow-400">
                                {supplier.contact_phone}
                              </a>
                            </p>
                          )}
                          {supplier.contact_email && (
                            <p className="text-gray-300 flex items-center gap-2">
                              <Mail className="w-3 h-3 text-gray-500" />
                              <a href={`mailto:${supplier.contact_email}`} className="hover:text-yellow-400">
                                {supplier.contact_email}
                              </a>
                            </p>
                          )}
                          {supplier.website_url && (
                            <p className="text-gray-300 flex items-center gap-2">
                              <Globe className="w-3 h-3 text-gray-500" />
                              <a
                                href={supplier.website_url.startsWith('http') ? supplier.website_url : `https://${supplier.website_url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-400 hover:text-blue-300 truncate"
                              >
                                {supplier.website_url}
                              </a>
                            </p>
                          )}
                          {supplier.address && (
                            <p className="text-gray-400 flex items-center gap-2 mt-2">
                              <MapPin className="w-3 h-3" />
                              {supplier.address}
                            </p>
                          )}
                        </div>
                        
                        {/* Supplier stats */}
                        {stats?.supplier_stats?.[supplier.name] && (
                          <div className="mt-3 pt-3 border-t border-gray-600 flex justify-between text-sm">
                            <span className="text-gray-400">
                              {stats.supplier_stats[supplier.name].purchase_count} innkjøp
                            </span>
                            <span className="text-green-400">
                              {formatCurrency(stats.supplier_stats[supplier.name].total_spent)}
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Building2 className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400">Ingen leverandører registrert</p>
                    <button
                      onClick={handleAddSupplier}
                      className="mt-4 text-yellow-500 hover:text-yellow-400"
                    >
                      Legg til din første leverandør
                    </button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Price History / Trends */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-yellow-500" />
                  Prishistorikk per kaliber
                </CardTitle>
              </CardHeader>
              <CardContent>
                {Object.keys(stats?.caliber_stats || {}).length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-gray-400 border-b border-gray-700">
                          <th className="text-left py-2 px-2">Kaliber</th>
                          <th className="text-right py-2 px-2">Totalt kjøpt</th>
                          <th className="text-right py-2 px-2">Gj.snitt pris</th>
                          <th className="text-right py-2 px-2">Siste pris</th>
                          <th className="text-right py-2 px-2">Siste kjøp</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(stats.caliber_stats)
                          .sort((a, b) => b[1].total_purchased - a[1].total_purchased)
                          .map(([caliber, data]) => (
                            <tr key={caliber} className="border-b border-gray-700/50">
                              <td className="py-3 px-2 text-white font-medium">{caliber}</td>
                              <td className="py-3 px-2 text-right text-gray-300">
                                {data.total_purchased?.toLocaleString()} stk
                              </td>
                              <td className="py-3 px-2 text-right text-gray-300">
                                {formatCurrency(data.avg_price)}/stk
                              </td>
                              <td className="py-3 px-2 text-right">
                                <span className={
                                  data.last_price > data.avg_price 
                                    ? 'text-red-400' 
                                    : data.last_price < data.avg_price 
                                      ? 'text-green-400' 
                                      : 'text-gray-300'
                                }>
                                  {formatCurrency(data.last_price)}/stk
                                </span>
                              </td>
                              <td className="py-3 px-2 text-right text-gray-400">
                                {formatDate(data.last_purchase_date)}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <TrendingUp className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400">Ingen prisdata tilgjengelig</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Supplier Modal */}
      <Dialog open={showSupplierModal} onOpenChange={setShowSupplierModal}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingSupplier ? 'Rediger leverandør' : 'Ny leverandør'}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Legg inn kontaktinformasjon for ammunisjonsleverandøren
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Navn *</label>
              <input
                type="text"
                value={supplierForm.name}
                onChange={(e) => setSupplierForm(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                placeholder="Leverandørnavn"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Kontaktperson</label>
                <input
                  type="text"
                  value={supplierForm.contact_person}
                  onChange={(e) => setSupplierForm(prev => ({ ...prev, contact_person: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                  placeholder="Fullt navn"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Telefon</label>
                <input
                  type="tel"
                  value={supplierForm.contact_phone}
                  onChange={(e) => setSupplierForm(prev => ({ ...prev, contact_phone: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                  placeholder="+47 ..."
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm text-gray-400 mb-1">E-post</label>
              <input
                type="email"
                value={supplierForm.contact_email}
                onChange={(e) => setSupplierForm(prev => ({ ...prev, contact_email: e.target.value }))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                placeholder="epost@leverandor.no"
              />
            </div>
            
            <div>
              <label className="block text-sm text-gray-400 mb-1">Nettside / Nettbutikk URL</label>
              <input
                type="url"
                value={supplierForm.website_url}
                onChange={(e) => setSupplierForm(prev => ({ ...prev, website_url: e.target.value }))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                placeholder="https://..."
              />
            </div>
            
            <div>
              <label className="block text-sm text-gray-400 mb-1">Adresse</label>
              <input
                type="text"
                value={supplierForm.address}
                onChange={(e) => setSupplierForm(prev => ({ ...prev, address: e.target.value }))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                placeholder="Gateadresse, postnummer, sted"
              />
            </div>
            
            <div>
              <label className="block text-sm text-gray-400 mb-1">Notater</label>
              <textarea
                value={supplierForm.notes}
                onChange={(e) => setSupplierForm(prev => ({ ...prev, notes: e.target.value }))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                rows={2}
                placeholder="Eventuelle notater..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <button
              onClick={() => setShowSupplierModal(false)}
              className="px-4 py-2 text-gray-400 hover:text-white"
            >
              Avbryt
            </button>
            <button
              onClick={handleSaveSupplier}
              className="px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400"
            >
              {editingSupplier ? 'Oppdater' : 'Opprett'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Purchase Modal */}
      <Dialog open={showPurchaseModal} onOpenChange={setShowPurchaseModal}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPurchase ? 'Rediger innkjøp' : 'Nytt innkjøp'}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Registrer ammunisjonsinnkjøp med kvitteringer og fakturaer
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Leverandør</label>
                <select
                  value={purchaseForm.supplier_id}
                  onChange={(e) => {
                    const supplier = suppliers.find(s => s.id === e.target.value);
                    setPurchaseForm(prev => ({
                      ...prev,
                      supplier_id: e.target.value,
                      supplier_name: supplier?.name || ''
                    }));
                  }}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                >
                  <option value="">Velg leverandør...</option>
                  {suppliers.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Eller skriv inn navn</label>
                <input
                  type="text"
                  value={purchaseForm.supplier_name}
                  onChange={(e) => setPurchaseForm(prev => ({ ...prev, supplier_name: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                  placeholder="Leverandørnavn"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Innkjøpsdato *</label>
                <input
                  type="date"
                  value={purchaseForm.purchase_date}
                  onChange={(e) => setPurchaseForm(prev => ({ ...prev, purchase_date: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Fakturanummer</label>
                <input
                  type="text"
                  value={purchaseForm.invoice_number}
                  onChange={(e) => setPurchaseForm(prev => ({ ...prev, invoice_number: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                  placeholder="F.eks. INV-2024-001"
                />
              </div>
            </div>
            
            {/* Calibers Table */}
            <div>
              <label className="block text-sm text-gray-400 mb-2">Ammunisjon</label>
              <div className="space-y-2">
                {purchaseForm.calibers.map((cal, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <select
                      value={cal.caliber}
                      onChange={(e) => updateCaliberRow(idx, 'caliber', e.target.value)}
                      className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                    >
                      <option value="">Velg kaliber...</option>
                      {COMMON_CALIBERS.map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                      <option value="other">Annet...</option>
                    </select>
                    {cal.caliber === 'other' && (
                      <input
                        type="text"
                        placeholder="Kaliber"
                        onChange={(e) => updateCaliberRow(idx, 'caliber', e.target.value)}
                        className="w-24 px-2 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm"
                      />
                    )}
                    <input
                      type="number"
                      value={cal.quantity}
                      onChange={(e) => updateCaliberRow(idx, 'quantity', e.target.value)}
                      placeholder="Antall"
                      className="w-24 px-2 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm"
                    />
                    <input
                      type="number"
                      step="0.01"
                      value={cal.unit_price}
                      onChange={(e) => updateCaliberRow(idx, 'unit_price', e.target.value)}
                      placeholder="Kr/stk"
                      className="w-24 px-2 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm"
                    />
                    {purchaseForm.calibers.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeCaliberRow(idx)}
                        className="p-2 text-red-400 hover:bg-gray-700 rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addCaliberRow}
                  className="text-sm text-yellow-500 hover:text-yellow-400"
                >
                  + Legg til kaliber
                </button>
              </div>
            </div>
            
            {/* Total */}
            <div className="flex justify-between items-center py-2 px-3 bg-gray-700/50 rounded-lg">
              <span className="text-gray-400">Beregnet total:</span>
              <span className="text-green-400 font-bold text-lg">
                {formatCurrency(calculateTotal())}
              </span>
            </div>
            
            {/* Documents */}
            <div>
              <label className="block text-sm text-gray-400 mb-2">
                Dokumenter (kvitteringer, fakturaer)
              </label>
              
              {purchaseForm.documents.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {purchaseForm.documents.map((doc, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2 px-3 py-2 bg-gray-700 rounded-lg"
                    >
                      <FileText className="w-4 h-4 text-blue-400" />
                      <a
                        href={doc}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:text-blue-300 text-sm"
                      >
                        Dokument {idx + 1}
                      </a>
                      <button
                        type="button"
                        onClick={() => setShowDocumentPreview(doc)}
                        className="p-1 text-gray-400 hover:text-white"
                        title="Forhåndsvis"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <a
                        href={doc}
                        download
                        className="p-1 text-gray-400 hover:text-green-400"
                        title="Last ned"
                      >
                        <Download className="w-4 h-4" />
                      </a>
                      <button
                        type="button"
                        onClick={() => handleDeleteDocument(doc)}
                        className="p-1 text-gray-400 hover:text-red-400"
                        title="Slett"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {editingPurchase?.id ? (
                <label className="flex items-center gap-2 px-4 py-3 bg-gray-700 border border-dashed border-gray-600 rounded-lg cursor-pointer hover:bg-gray-600 transition-colors">
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleDocumentUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                  {uploading ? (
                    <Loader2 className="w-5 h-5 animate-spin text-yellow-500" />
                  ) : (
                    <Upload className="w-5 h-5 text-gray-400" />
                  )}
                  <span className="text-gray-400">
                    {uploading ? 'Laster opp...' : 'Last opp kvittering eller faktura'}
                  </span>
                </label>
              ) : (
                <p className="text-gray-500 text-sm italic">
                  Lagre innkjøpet først for å kunne laste opp dokumenter
                </p>
              )}
            </div>
            
            {/* Notes */}
            <div>
              <label className="block text-sm text-gray-400 mb-1">Notater</label>
              <textarea
                value={purchaseForm.notes}
                onChange={(e) => setPurchaseForm(prev => ({ ...prev, notes: e.target.value }))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                rows={2}
                placeholder="Eventuelle notater..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <button
              onClick={() => setShowPurchaseModal(false)}
              className="px-4 py-2 text-gray-400 hover:text-white"
            >
              Avbryt
            </button>
            <button
              onClick={handleSavePurchase}
              className="px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400"
            >
              {editingPurchase ? 'Oppdater' : 'Lagre'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Preview Modal */}
      <Dialog open={!!showDocumentPreview} onOpenChange={() => setShowDocumentPreview(null)}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Forhåndsvisning</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto">
            {showDocumentPreview && (
              showDocumentPreview.toLowerCase().endsWith('.pdf') ? (
                <iframe
                  src={showDocumentPreview}
                  className="w-full h-[70vh]"
                  title="PDF Preview"
                />
              ) : (
                <img
                  src={showDocumentPreview}
                  alt="Document preview"
                  className="max-w-full max-h-[70vh] mx-auto"
                />
              )
            )}
          </div>
          <DialogFooter>
            <a
              href={showDocumentPreview}
              download
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-500"
            >
              <Download className="w-4 h-4 inline mr-2" />
              Last ned
            </a>
            <button
              onClick={() => setShowDocumentPreview(null)}
              className="px-4 py-2 text-gray-400 hover:text-white"
            >
              Lukk
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AmmoManagement;
